/**
 * Pure helpers for rendering a prospect's at-bat log.
 *
 * Kept out of the component so the two things that are easy to get wrong —
 * grouping by the ballpark's date and rendering the ballpark's clock rather than
 * the viewer's — are unit-testable.
 */

import type { AtBat, AtBatSummary } from '../types/at-bats';

export interface AtBatGame {
  /** ISO `yyyy-MM-dd` local date at the ballpark. */
  gameDate: string;
  atBats: AtBat[];
}

/**
 * Group a log into games, preserving the order the API returned.
 *
 * Groups on `gameDate` (the ballpark's local date, computed server-side) rather
 * than re-deriving a date from `occurredAt`. Deriving it here would use the
 * viewer's timezone, so a coach reviewing a Florida night game from Arizona would
 * see it split across two headings.
 */
export const groupAtBatsByGame = (atBats: AtBat[]): AtBatGame[] => {
  const games: AtBatGame[] = [];
  const byDate = new Map<string, AtBatGame>();

  for (const atBat of atBats) {
    const existing = byDate.get(atBat.gameDate);
    if (existing) {
      existing.atBats.push(atBat);
      continue;
    }
    const game: AtBatGame = { gameDate: atBat.gameDate, atBats: [atBat] };
    byDate.set(atBat.gameDate, game);
    games.push(game);
  }

  return games;
};

/**
 * The wall-clock time shown on the scoreboard when the at-bat happened, as a
 * `Date` whose UTC fields carry the ballpark's local values.
 *
 * Shifting the instant by the stored offset and then reading UTC getters is what
 * makes the rendered hour independent of where the log is being read from.
 */
const toBallparkClock = (occurredAt: string, offsetMinutes: number): Date =>
  new Date(new Date(occurredAt).getTime() + offsetMinutes * 60_000);

/**
 * Format the at-bat's local start time, e.g. `6:05 PM`. Returns an empty string
 * for an unparseable timestamp rather than rendering `Invalid Date`.
 */
export const formatAtBatTime = (occurredAt: string, offsetMinutes: number): string => {
  const clock = toBallparkClock(occurredAt, offsetMinutes);
  if (Number.isNaN(clock.getTime())) return '';

  return clock.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

/**
 * Format a game-date heading, e.g. `Jul 15, 2026`.
 *
 * Parsed as a UTC instant and formatted in UTC. Passing the bare `yyyy-MM-dd` to
 * `new Date()` already yields UTC midnight, so formatting in the viewer's zone
 * would render the previous day for anyone west of Greenwich.
 */
export const formatGameDate = (gameDate: string): string => {
  const parsed = new Date(`${gameDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return gameDate;

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/** Batting average as a leading-zero-less `.333`, or `—` when there are no at-bats. */
export const formatBattingAverage = (average: number | null): string => {
  if (average === null || average === undefined) return '—';
  return average.toFixed(3).replace(/^0\./, '.');
};

/**
 * The one-line roll-up above the log, e.g. `12 PA · 10 AB · 4 H · .400 · 2 BB · 3 K`.
 * Returns an empty string for an empty log so the caller can skip the line entirely.
 */
export const formatAtBatSummary = (summary: AtBatSummary): string => {
  if (summary.plateAppearances === 0) return '';

  const parts = [
    `${summary.plateAppearances} PA`,
    `${summary.atBats} AB`,
    `${summary.hits} H`,
  ];
  if (summary.battingAverage !== null) {
    parts.push(formatBattingAverage(summary.battingAverage));
  }
  parts.push(`${summary.walks} BB`, `${summary.strikeouts} K`);

  return parts.join(' · ');
};

/**
 * The device's current UTC offset in minutes east of UTC, in the sign convention
 * the API expects. `Date.prototype.getTimezoneOffset` returns the opposite sign.
 */
export const currentUtcOffsetMinutes = (): number => -new Date().getTimezoneOffset();

/**
 * The value for a `datetime-local` input: the ballpark's wall clock, so editing a
 * road game shows the time the coach actually saw on the scoreboard.
 */
export const toDateTimeLocalValue = (
  occurredAt: string,
  offsetMinutes: number,
): string => {
  const clock = toBallparkClock(occurredAt, offsetMinutes);
  if (Number.isNaN(clock.getTime())) return '';
  return clock.toISOString().slice(0, 16);
};

/**
 * Turn a `datetime-local` value back into a UTC instant.
 *
 * The offset is passed in rather than read from the browser: the input holds the
 * *ballpark's* wall clock, so reconstructing with the viewer's offset would shift
 * the instant whenever a coach reviews a road game from another timezone. For a
 * new entry the caller passes the device offset, which is the ballpark.
 *
 * Returns null for an unparseable value so callers can reject rather than persist
 * an Invalid Date.
 */
export const fromDateTimeLocalValue = (
  value: string,
  offsetMinutes: number,
): string | null => {
  if (!value) return null;
  // `datetime-local` yields `yyyy-MM-ddTHH:mm`, optionally with seconds.
  const withSeconds = value.length === 16 ? `${value}:00` : value;
  const asWallClock = Date.parse(`${withSeconds}Z`);
  if (Number.isNaN(asWallClock)) return null;
  return new Date(asWallClock - offsetMinutes * 60_000).toISOString();
};
